/**
 * 统一管理页面的 Hook
 */

import { useState, useCallback, useEffect } from 'react';
import { message, Modal } from 'antd';
import { aiConfigService } from '../../../services/aiConfigService';
import { serviceDiscoveryService } from '../../../services/serviceDiscoveryService';
import type { Component, Dataset } from '../../../services/aiConfigService';
import type { ServiceInfo } from '@ms/shared-auth';
import type { TreeNode } from '../utils/treeBuilder';

// Mock 微服务数据
const getMockServices = (): ServiceInfo[] => {
  return [
    {
      serviceName: 'msCentral',
      displayName: '中央服务',
      status: 'online',
      healthStatus: true,
      frontendUrl: 'http://localhost:30001',
      backendUrl: 'http://localhost:30002',
    },
    {
      serviceName: 'msHotel',
      displayName: '酒店服务',
      status: 'online',
      healthStatus: true,
      frontendUrl: 'http://localhost:30011',
      backendUrl: 'http://localhost:30012',
    },
  ];
};

export const useUnifiedManagement = () => {
  const [services, setServices] = useState<ServiceInfo[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  
  // 添加节点相关状态
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addModalType, setAddModalType] = useState<string>('');
  const [addModalParent, setAddModalParent] = useState<TreeNode | null>(null);
  
  // 节点详情编辑相关状态
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editingNode, setEditingNode] = useState<TreeNode | null>(null);
  const [formRef, setFormRef] = useState<any>(null);

  // 加载所有数据
  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      // 静默处理错误，使用 Mock 数据
      const [servicesData, componentsData, datasetsData] = await Promise.all([
        serviceDiscoveryService.getAllServices().catch((error) => {
          // CORS 错误或其他错误时使用 Mock 数据，不显示错误提示
          console.warn('获取微服务列表失败，使用 Mock 数据（CORS 或其他错误）');
          return getMockServices();
        }),
        aiConfigService.getComponentList().catch((error) => {
          // 静默处理，返回空数组
          console.warn('获取组件列表失败，使用空数组');
          return [];
        }),
        aiConfigService.getDatasetList().catch((error) => {
          // 静默处理，返回空数组
          console.warn('获取数据集列表失败，使用空数组');
          return [];
        }),
      ]);
      setServices(servicesData || getMockServices());
      setComponents(componentsData || []);
      setDatasets(datasetsData || []);
    } catch (error: any) {
      // 最后的兜底，确保页面可以正常显示
      console.warn('加载数据时发生未知错误，使用 Mock 数据');
      setServices(getMockServices());
      setComponents([]);
      setDatasets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // 选择节点
  const handleSelect = useCallback((keys: React.Key[], info: any) => {
    if (keys.length === 0) {
      setSelectedNode(null);
      setSelectedKeys([]);
      return;
    }
    setSelectedKeys(keys);
    // 从 info 中获取节点数据
    const nodeData = info.node?.data;
    if (nodeData) {
      // 构建完整的 TreeNode
      const node: TreeNode = {
        key: nodeData.key || info.node?.key || keys[0] as string,
        title: nodeData.title || info.node?.title || '',
        type: nodeData.type || info.node?.type || 'service',
        data: nodeData.data || nodeData,
        children: nodeData.children,
        isLeaf: nodeData.isLeaf,
      };
      setSelectedNode(node);
    } else {
      setSelectedNode(null);
    }
  }, []);

  // 展开/折叠节点
  const handleExpand = useCallback((keys: React.Key[]) => {
    setExpandedKeys(keys);
  }, []);

  // 添加节点
  const handleAddNode = useCallback((type: string, parent?: TreeNode) => {
    setAddModalType(type);
    setAddModalParent(parent || null);
    setAddModalVisible(true);
  }, []);

  // 添加成功
  const handleAddSuccess = useCallback(() => {
    setAddModalVisible(false);
    loadAllData();
    message.success('添加成功');
  }, [loadAllData]);

  // 关闭添加对话框
  const handleCloseAddModal = useCallback(() => {
    setAddModalVisible(false);
    setAddModalType('');
    setAddModalParent(null);
  }, []);

  // 编辑节点
  const handleEditNode = useCallback((node: TreeNode) => {
    setEditingNode(node);
    setDetailModalVisible(true);
  }, []);

  // 保存节点
  const handleSaveNode = useCallback(async () => {
    if (!formRef || !editingNode) return;
    try {
      const values = await formRef.validateFields();
      // TODO: 根据节点类型调用不同的保存 API
      console.log('保存节点:', editingNode.type, values);
      message.success('保存成功（Mock 模式）');
      setDetailModalVisible(false);
      setEditingNode(null);
      loadAllData();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || '保存失败');
    }
  }, [formRef, editingNode, loadAllData]);

  // 删除节点
  const handleDeleteNode = useCallback(async (node: TreeNode) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除${node.title}吗？`,
      onOk: async () => {
        try {
          // TODO: 根据节点类型调用不同的删除 API
          console.log('删除节点:', node.type, node.key);
          message.success('删除成功（Mock 模式）');
          loadAllData();
        } catch (error: any) {
          message.error(error?.message || '删除失败');
        }
      },
    });
  }, [loadAllData]);

  // 关闭详情对话框
  const handleCloseDetailModal = useCallback(() => {
    setDetailModalVisible(false);
    setEditingNode(null);
    formRef?.resetFields?.();
  }, [formRef]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  return {
    services,
    components,
    datasets,
    loading,
    selectedNode,
    expandedKeys,
    selectedKeys,
    addModalVisible,
    addModalType,
    addModalParent,
    detailModalVisible,
    editingNode,
    formRef,
    setFormRef,
    loadAllData,
    handleSelect,
    handleExpand,
    handleAddNode,
    handleAddSuccess,
    handleCloseAddModal,
    handleEditNode,
    handleSaveNode,
    handleDeleteNode,
    handleCloseDetailModal,
    setExpandedKeys,
  };
};
